import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Payslips() {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSalaries();
  }, []);

  const fetchSalaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/salary/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalaries(response.data);
    } catch (error) {
      toast.error('Failed to load payslips');
    } finally {
      setLoading(false);
    }
  };

  const generatePayslip = (salary) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const payslipContent = `
===========================================
           PAYSLIP - ${salary.month}
===========================================

Employee: ${user.full_name}
Email: ${user.email}
Designation: ${user.designation || 'N/A'}

-------------------------------------------
SALARY DETAILS
-------------------------------------------
Base Salary:          ₹${salary.base_salary.toFixed(2)}

ATTENDANCE SUMMARY
-------------------------------------------
Days Present:         ${salary.days_present}
Days Absent:          ${salary.days_absent}
Days Late:            ${salary.days_late}
Half Days:            ${salary.half_days.toFixed(2)}
Paid Leaves Used:     ${salary.paid_leaves_used}

DEDUCTIONS
-------------------------------------------
Total Deductions:     ₹${salary.deductions.toFixed(2)}

NET SALARY
-------------------------------------------
Final Salary:         ₹${salary.final_salary.toFixed(2)}

Processed At: ${new Date(salary.processed_at).toLocaleString()}

===========================================
    `;

    const blob = new Blob([payslipContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payslip-${salary.month}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Payslip downloaded');
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="payslips-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">My Payslips</h1>
          <p className="text-muted-foreground mt-2">View and download your monthly payslips</p>
        </div>

        <Card data-testid="payslips-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="mr-2 h-5 w-5" />
              Salary Records
            </CardTitle>
            <CardDescription>Your monthly salary history</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : salaries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No salary records found
              </div>
            ) : (
              <div className="w-full overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Base Salary</TableHead>
                      <TableHead>Present</TableHead>
                      <TableHead>Absent</TableHead>
                      <TableHead>Late</TableHead>
                      <TableHead>Deductions</TableHead>
                      <TableHead>Final Salary</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salaries.map((salary) => (
                      <TableRow key={salary.id} data-testid="salary-row">
                        <TableCell className="font-medium">{salary.month}</TableCell>
                        <TableCell>₹{salary.base_salary.toFixed(2)}</TableCell>
                        <TableCell>{salary.days_present}</TableCell>
                        <TableCell>{salary.days_absent}</TableCell>
                        <TableCell>{salary.days_late}</TableCell>
                        <TableCell className="text-red-600">₹{salary.deductions.toFixed(2)}</TableCell>
                        <TableCell className="font-bold">₹{salary.final_salary.toFixed(2)}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => generatePayslip(salary)}
                            data-testid="download-payslip-button"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
